import {supportedProviders, isSupportedProvider} from './shared/providers.js';
import {supportedTypes, isSupportedType} from './map/types.js';
import {ConfigurationError, DealerLocatorError} from './shared/errors.js';
import {brandsById} from './shared/brands.js';
import {formatDistance} from './shared/units.js';
import translationsDefault from './translations-default.json';
import iframeContent from '.tmp/iframe.html?raw';
import Api from './api/api.js';

/**
 * The library creates a separate `conti` namespace for itself to prevent possible collision
 * with other libraries.
 *
 * You can access the namespace on the window object:
 *
 * ```
 * window.conti
 * ```
 *
 * @namespace conti
 */

/**
 * A callback that gets triggered when the location changed.
 *
 * @callback onLocationChange
 * @param {object} data The data object.
 * @property {array} data.latLng The latitude and longitude of the location.
 * @property {string} data.formattedAddress A formatted text string for the location. Not always available.
 * @property {string} data.geocoderResult The full response from the geocoder. Not always available.
 */

/**
 * A callback that gets triggered when the nearest dealers have been fetched.
 *
 * @callback onNearestDealersFetch
 * @param {array} dealers The dealers array.
 */

/**
 * Main class for configuration, initialization and rendering of the Dealer Locator application.
 *
 * Basic usage example where `container` is the ID of the HTMLElement to render into:
 *
 * ```
 * const dealerLocator = new conti.DealerLocator({
 *   container: "map"
 * });
 * dealerLocator.render();
 * ```
 *
 * For more advanced usage examples, [see here](tutorial-usage-examples.html).
 *
 * @param {object} options The configuration object.
 * @property {string} options.container The ID of the HTMLElement to render into.
 * @property {string} [options.apiToken] Overrides the pre-configured token for the backend API.
 * @property {string} [options.googleApiKey] Overrides the pre-configured API key for the Google API.
 * @property {string} [options.bingApiKey] Overrides the pre-configured API key for the Bing API.
 * @property {string} [options.environment=staging] Sets the environment configuration. Can be one of 'development', 'staging' or 'production'. This configures the API endpoint and API token.
 * @property {string} [options.mapProvider=google] The map provider. Can be one of 'google' or 'bing'.
 * @property {string} [options.mapType=roadmap] The type of the map. Can be one of 'roadmap' or 'satellite'.
 * @property {number} [options.zoom=3] The initial zoom level of the map.
 * @property {array<number>} [options.latLng=[35,10]] The initial latitude and longitude position of the map. Can be overridden by locationText.
 * @property {string} [options.locationText] The initial location of the map in form of a search string (example: 'Munich, Bavaria').
 *  If provided this is used instead of the latLng. When no matching location for the search string was found, it will fall back to the latLng.
 * @property {boolean} [options.locateInitially] Whether to locate the user initially via the Browser geolocation API.
 *   If `true`, this is preferred over the locationText and the latLng. When the user doesn’t provide this, it falls back to locationText or latLng in this order. The default is `false`.
 * @property {array<string>} [options.marketIds] Limits the dealers displayed to specific markets. Can be one, many or none. If none is provided, all dealers for all markets are shown but cannot be filtered.
 * @property {string} [options.businessUnitId] The business unit id. Limits dealers to the passed in business unit. If none is provided, dealers for all business units are shown. Possible values: 'plt', 'transport', 'specialtytires', 'agriculture', 'material-handling', 'earthmoving', 'motorcycle', 'bicycle', 'fleet'.
 * @property {string} [options.brandId=continental] The brand id.
 * @property {string} [options.locale=en] The locale. This is used for convertion of units and numbers (for example km/miles) and handling of right-to-left display languages.
 *  It must be a BCP 47 language code. Examples: 'en', 'en-US', 'de-DE', 'de-AT'
 * @property {array<string>} [options.dealerAttributes] Initial attributes to filter the dealers by.
 * @property {boolean} [options.featuredDealersOnly=false] Initial value of the featured dealer filter. If true only featured dealers are queried and the featured dealer filter is hidden so that it can't be deactivated by the user.
 * @property {object} [options.translations] The translations dictionary used for text translations. The language will fall back to english when this is undefined.
 *  For more informations on the available translation keys, [see here](tutorial-translations.html).
 * @property {onLocationChange} [options.onLocationChange] A callback that gets triggered when the location changed.
 * @property {fonts} [options.fonts] An object to configure the fonts to use.
 * @property {string} options.fonts.default The absolute URL to the font file used as the default font.
 * @property {string} options.fonts.bold The absolute URL to the font file used as the bold font.
 * @property {string} options.fonts.light The absolute URL to the font file used as the light font.
 * @property {string} [options.mode] The mode for the UI: either 'desktop-compact' or 'auto' (the default). 'auto' will render the desktop or mobile view depending on the screen size. 'desktop-compact' will render a compact version of the desktop mode and will not collapse to the mobile mode.
 * @property {boolean} [options.disableAutocompleteDealers] Set to true (default = false) to explicitly disable autocomplete suggestions for dealers when typing in the search field.
 * @property {boolean} [options.disableAutocompleteLocations] Set true (default = false) to explicitly disable autocomplete suggestions for locations using the Google Places Autocomplete API. Please note that location suggestions are disabled automatically when 'mapProvider' is not set to 'google'.
 * @memberof conti
 */
class DealerLocator {
  /**
   * Initializes the Dealer Locator with the given configuration options.
   *
   * __Note:__ The Dealer Locator will not be rendered into the DOM until you call
   * the `.render()` function on the newly created instance.
   *
   * @param {object} options The configuration object.
   */
  constructor(options = {}) {
    this.options = this._mergeDefaults(options);
    this.iframe = null;
    this._validate();
    window.conti._dealerLocator = this; // We need this as a global to reference it later on from inside the iframe
  }

  /**
   * Get the closest dealers for a location. Either the optional passed in location is used, otherwise it falls back to the location passed into the constructor. locationText or the browser location won’t be used here.
   *
   * The brandId, marketIds, businessUnitId, dealerAttributes and locale options passed in the constructor will be used for this API endpoint, too.
   *
   * @param {array<number>} latLng An optional location that can override the latLng passed into the constructor.
   * @param {onNearestDealersFetch} callback A callback returning with the closest 3 dealers.
   */
  getClosestDealers(latLng, callback) {
    const api = new Api(this.options.environment, this.options.apiToken);
    const {
      brandId,
      businessUnitId,
      marketIds,
      dealerAttributes,
      featuredDealersOnly,
      locale
    } = this.options;

    const options = {
      latLng: latLng || this.options.latLng,
      brandId,
      businessUnitId,
      marketIds,
      attributes: dealerAttributes,
      maxDealers: 3,
      featuredDealersOnly
    };

    api.fetchClosestDealers(options, dealers => {
      dealers = dealers.map(dealer => {
        dealer.distance = formatDistance(dealer.distance, locale);
        return dealer;
      });

      callback(dealers);
    });
  }

  /**
   * Renders the Dealer Locator app into the DOM. This must always be called after construction
   * of a new Dealer Locator instance or when any of the instance properties have been updated.
   */
  render() {
    this.options = this._mergeDefaults(this.options);
    this.container = document.getElementById(this.options.container);

    if (!this.container) {
      throw new DealerLocatorError(
        `Couldn’t find container with id '${this.options.container}'`
      );
    }

    if (this.iframe) {
      this.container.removeChild(this.iframe);
    }

    this.iframe = this._createIframe();
    this.container.appendChild(this.iframe);

    setTimeout(() => {
      const iframeDocument =
        this.iframe.contentDocument || this.iframe.contentWindow.document;
      iframeDocument.open();
      iframeDocument.write(iframeContent);
      iframeDocument.close();
    }, 1);
  }

  _createIframe() {
    const iframeElement = document.createElement('iframe');

    iframeElement.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-top-navigation allow-forms allow-popups allow-popups-to-escape-sandbox'
    );
    iframeElement.setAttribute('data-framedomain', document.location.origin);
    iframeElement.style.width = '100%';
    iframeElement.style.height = '100%';
    iframeElement.style.border = 'none';
    iframeElement.style.display = 'block';

    return iframeElement;
  }

  _mergeDefaults(options) {
    const environment = options.environment || 'staging';

    return {
      container: options.container || null,
      apiToken: options.apiToken || null,
      googleApiKey: options.googleApiKey || null,
      bingApiKey: options.bingApiKey || null,
      environment,
      mapProvider: options.mapProvider || 'google',
      mapType: options.mapType || 'roadmap',
      zoom: options.zoom || 3,
      latLng: options.latLng || [35, 10],
      locationText: options.locationText || null,
      locateInitially: options.locateInitially || false,
      marketIds: options.marketIds || [],
      brandId: options.brandId || 'continental',
      businessUnitId: options.businessUnitId || null,
      locale: options.locale || 'en',
      dealerAttributes: options.dealerAttributes || [],
      featuredDealersOnly: Boolean(options.featuredDealersOnly),
      translations: this._transformTranslations(options.translations) || null,
      onLocationChange: options.onLocationChange || function () {},
      fonts: options.fonts || {},
      mode: options.mode || 'auto',
      disableAutocompleteDealers: Boolean(options.disableAutocompleteDealers),
      disableAutocompleteLocations: Boolean(
        options.disableAutocompleteLocations
      )
    };
  }

  _validate() {
    if (
      this.options.mapProvider &&
      !isSupportedProvider(this.options.mapProvider)
    ) {
      throw new ConfigurationError(
        `Map provider must be one of ${supportedProviders}`
      );
    }

    if (this.options.mapType && !isSupportedType(this.options.mapType)) {
      throw new ConfigurationError(`Map type must be one of ${supportedTypes}`);
    }

    if (this.options.zoom && isNaN(this.options.zoom)) {
      throw new ConfigurationError('zoom must be a number');
    }

    if (
      this.options.latLng &&
      (this.options.latLng[0] < -90 || this.options.latLng[0] > 90)
    ) {
      throw new ConfigurationError('Map latitude must be between -90 and 90');
    }

    if (
      this.options.latLng &&
      (this.options.latLng[1] < -180 || this.options.latLng[1] > 180)
    ) {
      throw new ConfigurationError(
        'Map longitude must be between -180 and 180'
      );
    }

    if (!brandsById[this.options.brandId]) {
      throw new DealerLocatorError(
        `Unknown brand id '${this.options.brandId}'`
      );
    }
  }

  _transformTranslations(translations) {
    if (!translations) {
      return null;
    }

    const newTranslations = {};

    for (const key in translations) {
      if (Object.prototype.hasOwnProperty.call(translations, key)) {
        const keys = key.split('.');

        if (keys.length === 2) {
          const mainKey = keys[0];
          const nestedKey = keys[1];

          if (!newTranslations[mainKey]) {
            newTranslations[mainKey] = {};
          }

          newTranslations[mainKey][nestedKey] = translations[key];
        } else {
          newTranslations[key] = translations[key];
        }
      }
    }

    ['activeFilters', 'filteredResults'].forEach(mainKey => {
      if (!newTranslations[mainKey]) {
        return;
      }

      ['none', 'one', 'many'].forEach(nestedKey => {
        if (!newTranslations[mainKey][nestedKey]) {
          newTranslations[mainKey][nestedKey] =
            translationsDefault[mainKey][nestedKey];
        }
      });
    });

    return newTranslations;
  }
}

export default DealerLocator;
